import { CLOSE_ANIMATION_DURATION } from "shared/components/wrappers/drawer/drawerHelper";
import useHelpers from "composables/useHelpers";
import { storeToRefs } from "pinia";
import { useModuleStore } from "store/modules/moduleStore";
import { useGlobalStore } from "stores/global/global";
import { createRouter, createWebHistory } from "vue-router";

// Import routes from modules
import { settingsIcon, toClassicIcon } from "common/defaultIcons";
import { useMenuStore } from "store/modules/menuStore.js";
import { useNotificationStore } from "store/modules/notificationStore";
import { useSettingsStore } from "store/modules/settingStore.js";
import modules from "./modules";
import { schoolAdminRoutes } from "core/schools/routes";
import { registerClassroomTabPersistence } from "classroom/routes/lastActiveTab";
import { ROUTES, USER_ROLES } from "shared/constants";
import {
  isAuthorized,
  isRouteAccessibleByName,
  pickFirstAccessibleChild,
  registerRouter,
} from "./routeAccess";

const { mappedTerms, schoolId, checkRole, generateMenuTitle } = useHelpers();

// TODO: Change all the route names to the lowercase
// TODO: Base all route changes on the route name not on the path
// TODO: Define all routes with children in separate modules to keep the files clean
const routes = [
  {
    path: "/",
    name: "Start",
    component: () => import("pages/Startpage.vue"),
    meta: {
      title: `Welkom bij Catwise`,
    },
  },
  {
    path: "/styleguide",
    name: "styleguide",
    component: () => import("pages/Styleguide.vue"),
    meta: { title: "Styleguide" },
  },
  {
    path: "/api-tester",
    name: "ApiTesterImpersonating",
    component: () => import("pages/admin/ApiTester.vue"),
    meta: {
      title: "API Tester",
      isMenuItem: false,
      icon: "fa-solid fa-code",
      layout: "FullScreenLayout",
      onlyWhenImpersonating: true,
    },
  },
  {
    path: "/settings",
    name: "settings",
    component: () => import("pages/info/Settings.vue"),
    meta: {
      title: "Persoonlijke instellingen",
      isMenuItem: true,
      icon: toClassicIcon(settingsIcon),
      menuSection: "profile",
    },
  },
  {
    path: "/notifications",
    name: "notifications",
    component: () => import("pages/info/Notifications.vue"),
    meta: { title: "Notificaties" },
  },
  {
    path: "/student-form",
    name: "studentForm",
    component: () => import("pages/info/StudentForm.vue"),
    meta: { title: "Feedback formulier" },
  },
  {
    path: "/learning-objectives",
    name: ROUTES.MY_LEARNING_OBJECTIVES,
    component: () => import("pages/student/LearningObjectives.vue"),
    meta: {
      title: "Mijn leerdoelen",
      sublabel: "bèta",
      isMenuItem: true,
      menuSection: "main",
      menuOrder: 7,
      roleRequired: ["student"],
      settingRequired: ["learningObjectives"],
      icon: "/images/menu/learning-objectives.svg",
      studentView: true,
    },
  },
  ...schoolAdminRoutes,
  {
    path: "/edit-student/:id",
    name: "EditStudent",
    component: () => import("pages/manage/data/AddOrUpdateStudent.vue"),
    meta: {
      title: `${mappedTerms.students[0].toUpperCase()}${mappedTerms.students.slice(
        1,
      )} beheren`,
      roleRequired: [USER_ROLES.WEBMASTER, USER_ROLES.MANAGER],
    },
  },
  {
    path: "/add-employee",
    name: "AddEmployee",
    component: () => import("pages/manage/data/AddOrUpdateEmployee.vue"),
    meta: {
      title: "Medewerker toevoegen",
      roleRequired: [USER_ROLES.WEBMASTER, USER_ROLES.MANAGER],
    },
  },
  {
    path: "/edit-employee/:id",
    name: "EditEmployee",
    component: () => import("pages/manage/data/AddOrUpdateEmployee.vue"),
    meta: {
      title: "Medewerker beheren",
      roleRequired: [USER_ROLES.WEBMASTER, USER_ROLES.MANAGER],
    },
  },
  {
    path: "/unauthorized",
    name: "Unauthorized",
    component: () => import("pages/Unauthorized.vue"),
    meta: {
      title: "403 Oeps, geen toegang...",
    },
  },
  {
    path: "/404",
    name: "error",
    component: () => import("pages/Error.vue"),
    meta: { title: "404 Pagina niet gevonden" },
  },
  // Add module routes before the catch-all
  ...modules,
  {
    // Catch all route - must be last
    path: "/:pathMatch(.*)*",
    name: "404",
    redirect: { name: "error" },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

registerRouter(router);
registerClassroomTabPersistence(router);

router.afterEach(async (to) => {
  document.title = `${generateMenuTitle(to)} - Catwise`;

  const notificationStore = useNotificationStore();
  const { executeNotificationActions } = notificationStore;

  await executeNotificationActions();
});

router.beforeEach(async (to) => {
  const menuStore = useMenuStore();

  if (menuStore.menuIsOpen) {
    menuStore.closeMenu();
    await new Promise((resolve) =>
      setTimeout(resolve, CLOSE_ANIMATION_DURATION),
    );
  }

  let token = localStorage.getItem("user_token");
  let schoolChoices = localStorage.getItem("school_choices");

  // Not authenticated -> Go to in
  if (
    token === null &&
    to.name !== "login" &&
    to.name !== "Inloggen" &&
    to.name !== "forgotPassword" &&
    to.name !== "resetPassword" &&
    to.name !== "twoFactorAuthentication" &&
    to.name !== "loginByEmail"
  ) {
    return { name: "login" };
  }

  // Authenticated and school choices -> Go to school login choice
  if (
    to.name !== "schoolLoginChoice" &&
    token !== null &&
    schoolChoices !== null
  ) {
    return { name: "schoolLoginChoice" };
  }

  // Authenticated and school choice is set -> Go to start
  if (to.name === "schoolLoginChoice" && schoolChoices === null) {
    return { name: "Start" };
  }

  // Authenticated -> Go to start
  if ((to.name === "login" || to.name === "loginByEmail") && token !== null) {
    return { name: "Start" };
  }

  // Get modules
  if (schoolId !== null && token !== null) {
    // TODO: Put this inside the components, this is a heavy load on every route change
    const globalStore = useGlobalStore();
    const { getSchoolModules, getSchool } = globalStore;
    const { hasFetchedSchoolData, hasFetchedModuleData } =
      storeToRefs(globalStore);

    try {
      if (!hasFetchedSchoolData.value) {
        await getSchool();
      }

      if (!hasFetchedModuleData.value) {
        await getSchoolModules();
      }

      const settingStore = useSettingsStore();
      const { getSchoolSettings } = settingStore;

      await getSchoolSettings();
    } catch (error) {
      console.log("🚀 ~ router.beforeEach ~ error:", error);

      // If we get a 401 or network error, the API interceptor will handle the redirect
      // Don't continue with route navigation if auth failed
      if (error?.response?.status === 401) {
        return false;
      }
    }

    const moduleStore = useModuleStore();
    const { hasSettingEnabled } = moduleStore;

    if (to.name === "manageSettings") {
      if (
        checkRole("decaan") &&
        hasSettingEnabled("portfolio") &&
        isRouteAccessibleByName(router, "manageSettingsPortfolio")
      ) {
        return { name: "manageSettingsPortfolio" };
      }

      if (
        !checkRole("decaan") &&
        isRouteAccessibleByName(router, "manageSettingsExpertGroups")
      ) {
        return { name: "manageSettingsExpertGroups" };
      }

      return (
        pickFirstAccessibleChild(router, "manageSettings") ?? {
          name: "Unauthorized",
        }
      );
    }

    if (
      (to.name === "studentMyFeedback" ||
        to.name === "studentMeetingDetail" ||
        to.name === "studentTransitionMeetingDetail" ||
        to.name === "MyStudents" ||
        to.name === "myMeetings" ||
        // The myMeetings parent redirects to a tab child, so a guard on the
        // parent name alone would never fire.
        to.name === "myMeetingsOpen" ||
        to.name === "myMeetingsCompleted") &&
      !hasSettingEnabled("studentMeeting")
    ) {
      return { name: "Unauthorized" };
    }
  }

  if (!router.hasRoute(to.name)) {
    return { name: "error" };
  }

  if (!isAuthorized(to)) {
    return { name: "Unauthorized" };
  }

  // Check ability-based permissions (async resolvers from domain modules)
  const abilityKey = to.meta?.abilityRequired;

  if (abilityKey) {
    const { abilityResolvers } = await import("shared/constants");
    const resolver = abilityResolvers[abilityKey];

    if (resolver && (await resolver()) === false) {
      return { name: "Unauthorized" };
    }
  }
});

// Track if we're redirecting to login to avoid reload loops
let isRedirectingToLogin = false;

router.onError((err) => {
  const msg = String(err?.message || "");

  // Don't reload if we're already redirecting to login due to 401
  if (isRedirectingToLogin) {
    return;
  }

  // Vite/Rollup errors commonly match these
  if (
    msg.includes("Failed to load module script") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.match(/Loading chunk \d+ failed/)
  ) {
    // Check if this is caused by a 401 redirect that's in progress
    // by checking if we're being redirected to login
    const currentPath = window.location.pathname;

    if (currentPath === "/login" || currentPath.includes("/login")) {
      isRedirectingToLogin = true;

      return;
    }

    window.location.reload();
  }
});

export default router;
